import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Fire,
  TrendUp,
  Star,
  Clock,
  Buildings,
  CurrencyDollar,
  BookOpen,
  Robot,
  CalendarBlank,
  GraduationCap,
  Pulse,
  Lightning,
  Wrench,
  Brain,
  Rocket,
  ChartLineUp,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import InfiniteNewsFeed from "@/components/InfiniteNewsFeed";

import { fetchAISection, fetchTrending } from "@/lib/api";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { useScrollObserver } from "@/hooks/useScrollObserver";
import { stripHtml } from "@/lib/stripHtml";
import { relativeTime } from "@/lib/time";

// ── Tiny helpers ──────────────────────────────────────────────────────────────

function isNewToday(item) {
  if (!item?.created_at) return false;
  return Date.now() - new Date(item.created_at).getTime() < 24 * 60 * 60 * 1000;
}

function TodayBadge() {
  return (
    <span className="inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded bg-sky-500 text-white uppercase tracking-wide shrink-0">
      Today
    </span>
  );
}

function Badge({ children, color = "blue" }) {
  const colors = {
    blue:   "bg-blue-50 text-blue-700 border-blue-200",
    red:    "bg-red-50 text-red-700 border-red-200",
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    slate:  "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${colors[color]}`}>
      {children}
    </span>
  );
}

function HotBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white shrink-0">
      <Fire size={8} weight="fill" /> HOT
    </span>
  );
}

function NewBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white shrink-0">
      NEW
    </span>
  );
}

// ── Section header with visible pill CTA ─────────────────────────────────────

function SectionHeader({ title, subtitle, href, hrefLabel = "View all" }) {
  return (
    <div className="flex items-start justify-between mb-5 gap-3">
      <div>
        <h2 className="text-xl font-bold text-slate-900 leading-snug">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {href && (
        <Link
          to={href}
          className="shrink-0 mt-0.5 inline-flex items-center gap-1 border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-400 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400/30"
        >
          {hrefLabel} <ArrowRight size={11} />
        </Link>
      )}
    </div>
  );
}

// ── Pulse Ticker ──────────────────────────────────────────────────────────────

function PulseTicker({ items }) {
  const [idx, setIdx] = useState(0);
  const pulseColors = {
    model:    "text-blue-400",
    funding:  "text-emerald-400",
    startup:  "text-purple-400",
    paper:    "text-amber-400",
    event:    "text-rose-400",
    tool:     "text-cyan-400",
    news:     "text-sky-400",
    research: "text-amber-400",
  };

  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 3500);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return (
    <div className="flex items-center gap-3 bg-white/5 border border-white/10 text-white/50 px-4 py-2.5 rounded-xl">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
      </span>
      <span className="text-xs">Loading live signals…</span>
    </div>
  );

  const current = items[idx];
  const text = current.text || current.title || current.summary || "";
  const time = current.time || current.date || "";
  const type = current.type || "news";

  return (
    <div className="flex items-center gap-3 bg-white/5 border border-white/10 text-white px-4 py-2.5 rounded-xl overflow-hidden">
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
        </span>
        <span className="text-xs font-bold text-white/60 uppercase tracking-widest">Live</span>
      </div>
      <div className="flex-1 overflow-hidden min-w-0">
        <div key={idx} className="animate-[fadeIn_0.4s_ease] flex items-center gap-2 min-w-0">
          <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${pulseColors[type] || "text-white"}`}>
            {type}
          </span>
          <span className="text-xs text-white/80 truncate">{text}</span>
          {time && <span className="text-[10px] text-white/40 shrink-0">{time}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Signal ${i + 1}`}
            className={`h-1 rounded-full transition-all ${i === idx ? "bg-white w-4" : "bg-white/20 w-1"}`}
          />
        ))}
      </div>
    </div>
  );
}

// ── Hero News Slider ──────────────────────────────────────────────────────────

function HeroNewsSlider({ items }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const slides = items.slice(0, 10);

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    const t = setInterval(() => setCurrent((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length, paused]);

  const prev = () => setCurrent((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setCurrent((i) => (i + 1) % slides.length);

  if (!slides.length) {
    return (
      <div className="h-[300px] rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-blue-400/40 border-t-blue-400 animate-spin" />
          <span className="text-white/30 text-sm">Loading latest stories…</span>
        </div>
      </div>
    );
  }

  const slide = slides[current];
  const slug = slide.slug || slide.id;
  const to = `/trending-ai/news/${slug}`;

  return (
    <div
      className="relative rounded-xl overflow-hidden bg-white/5 border border-white/10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Progress bar */}
      {!paused && (
        <div
          key={current}
          className="absolute top-0 left-0 h-0.5 bg-blue-400 z-20"
          style={{ animation: "progress-bar 5s linear forwards" }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Left: image */}
        <div className="relative h-[220px] md:h-[320px]">
          {slide.thumbnail ? (
            <img
              key={`img-${current}`}
              src={slide.thumbnail}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 flex items-center justify-center">
              <span className="text-white/10 font-black" style={{ fontSize: "80px" }}>
                {String(current + 1).padStart(2, "0")}
              </span>
            </div>
          )}
          {/* Gradient fade into right panel */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-slate-900/70 hidden md:block" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent md:hidden" />
          {/* Slide counter badge */}
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
            </span>
            <span className="text-[10px] font-bold text-white/80 uppercase tracking-wider">Live</span>
            <span className="text-[10px] text-white/40">{current + 1}/{slides.length}</span>
          </div>
        </div>

        {/* Right: descriptive content */}
        <div
          key={`desc-${current}`}
          className="p-6 md:p-7 flex flex-col justify-between"
          style={{ animation: "fadeIn 0.35s ease" }}
        >
          <div>
            {/* Badges */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {slide.category && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/20">
                  {slide.category}
                </span>
              )}
              {slide.hot && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white">
                  <Fire size={8} weight="fill" /> HOT
                </span>
              )}
              {isNewToday(slide) && <TodayBadge />}
            </div>

            {/* Title */}
            <Link to={to} className="block group mb-3">
              <h3 className="text-lg md:text-xl font-bold text-white group-hover:text-blue-300 leading-snug transition-colors line-clamp-3">
                {stripHtml(slide.title)}
              </h3>
            </Link>

            {/* Excerpt / description */}
            {(slide.excerpt || slide.summary) && (
              <p className="text-sm text-white/55 leading-relaxed line-clamp-3 md:line-clamp-4">
                {stripHtml(slide.excerpt || slide.summary)}
              </p>
            )}
          </div>

          {/* Meta + CTA */}
          <div className="mt-5">
            <div className="flex items-center gap-2 text-xs text-white/35 mb-4 flex-wrap">
              {slide.publisher && (
                <span className="font-semibold text-white/55">{slide.publisher}</span>
              )}
              {slide.publisher && slide.published && <span>·</span>}
              {slide.published && <span>{relativeTime(slide.published)}</span>}
              {slide.views != null && (
                <span className="flex items-center gap-1 ml-auto">
                  <TrendUp size={10} /> {Number(slide.views).toLocaleString()}
                </span>
              )}
            </div>
            <Link
              to={to}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400/30"
            >
              Read full story <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom navigation bar */}
      <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-4 bg-black/20">
        {/* Dot indicators */}
        <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Story ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current
                  ? "bg-blue-400 w-6"
                  : "bg-white/20 w-1.5 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
        {/* Prev / Next arrows */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={prev}
            aria-label="Previous story"
            className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ArrowRight size={13} className="text-white rotate-180" />
          </button>
          <button
            onClick={next}
            aria-label="Next story"
            className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ArrowRight size={13} className="text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hero — TOI-style featured section ────────────────────────────────────────

function AIFeaturedHero({ news, counts, pulseItems }) {
  const hero  = news[0] ?? null;
  const links = news.slice(1, 7);
  const totalRaisedB = counts.fundingTotal ? `$${counts.fundingTotal}B` : null;

  const chips = [
    { label: `${counts.models    || "50+"}  Models`,    to: "/trending-ai/models"    },
    { label: `${counts.startups  || "200+"} Startups`,  to: "/trending-ai/startups"  },
    { label: `${counts.companies || "100+"} Companies`, to: "/trending-ai/companies" },
    { label: `${counts.events    || "20+"}  Events`,    to: "/trending-ai/events"    },
    ...(totalRaisedB ? [{ label: `${totalRaisedB} Raised`, to: "/trending-ai/funding" }] : []),
  ];

  const heroSlug = hero ? (hero.slug || hero.id) : "";
  const heroTo   = hero ? `/trending-ai/news/${heroSlug}` : "#";

  return (
    <div className="space-y-4">
      {/* Eyebrow + stat chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 shrink-0">
          <Lightning size={12} className="text-blue-600" weight="fill" />
          <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-blue-600">AI Intelligence</span>
        </div>
        <span className="w-px h-4 bg-slate-200 shrink-0" />
        {chips.map(({ label, to }) => (
          <Link
            key={to}
            to={to}
            className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 border border-blue-100 text-blue-700 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors"
          >
            {label} <ArrowRight size={9} />
          </Link>
        ))}
      </div>

      {/* TOI hero grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: featured story */}
        <div className="lg:col-span-8">
          {hero ? (
            <Link
              to={heroTo}
              className="group block bg-white border border-slate-200 hover:border-blue-300 rounded-2xl overflow-hidden transition-all duration-200 hover:shadow-md"
            >
              {hero.thumbnail && (
                <div className="aspect-video w-full overflow-hidden bg-slate-100">
                  <img
                    src={hero.thumbnail}
                    alt={hero.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    onError={(e) => { e.target.parentElement.style.display = "none"; }}
                  />
                </div>
              )}
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {hero.category && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                      {hero.category}
                    </span>
                  )}
                  {hero.hot && <HotBadge />}
                  {isNewToday(hero) && <TodayBadge />}
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900 group-hover:text-blue-700 leading-snug transition-colors mb-3 line-clamp-3">
                  {stripHtml(hero.title)}
                </h1>
                {(hero.excerpt || hero.summary) && (
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-3 mb-4">
                    {stripHtml(hero.excerpt || hero.summary)}
                  </p>
                )}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    {hero.publisher && <span className="font-semibold text-slate-500">{hero.publisher}</span>}
                    {hero.publisher && hero.published && <span>·</span>}
                    {hero.published && <span>{relativeTime(hero.published)}</span>}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-blue-600 text-xs font-semibold group-hover:gap-2.5 transition-all">
                    Read full story <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </Link>
          ) : (
            <div className="bg-slate-100 rounded-2xl animate-pulse h-[420px]" />
          )}
        </div>

        {/* Right: quick links */}
        <div className="lg:col-span-4">
          <div className="border border-slate-200 rounded-2xl overflow-hidden h-full flex flex-col">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Top AI Headlines</span>
              <span className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-600 uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 live-dot" aria-hidden="true" />
                Live
              </span>
            </div>
            <div className="divide-y divide-slate-100 flex-1">
              {links.length > 0 ? links.map((item, i) => {
                const itemSlug = item.slug || item.id;
                return (
                  <a
                    key={item.id || i}
                    href={`/trending-ai/news/${itemSlug}`}
                    className="flex gap-3 px-4 py-3 hover:bg-slate-50 group transition-colors"
                  >
                    <span className="text-[11px] font-bold text-blue-500 shrink-0 w-4 mt-0.5">{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-700 line-clamp-2 leading-snug transition-colors">
                        {stripHtml(item.title)}
                      </p>
                      {(item.publisher || item.category) && (
                        <span className="text-[11px] text-slate-400 mt-0.5 block">
                          {item.publisher || item.category}
                        </span>
                      )}
                    </div>
                  </a>
                );
              }) : [...Array(5)].map((_, i) => (
                <div key={i} className="px-4 py-3 animate-pulse">
                  <div className="h-3 bg-slate-100 rounded w-4/5 mb-1.5" />
                  <div className="h-2 bg-slate-100 rounded w-1/3" />
                </div>
              ))}
            </div>
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
              <Link
                to="/trending-ai/news"
                className="w-full inline-flex items-center justify-center gap-1.5 border border-blue-200 hover:bg-blue-50 text-blue-700 font-semibold py-2 text-xs rounded-lg transition-colors"
              >
                See all AI news <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* PulseTicker */}
      <div className="bg-slate-900 rounded-xl px-4 py-2.5">
        <PulseTicker items={pulseItems} />
      </div>
    </div>
  );
}

// ── QuickStats — "Explore the Platform" destination cards ─────────────────────

function ExplorePlatform({ counts, compact = false }) {
  const destinations = [
    { label: "AI Models",       value: counts.models    || "50+",  icon: Brain,        color: "text-blue-600",  bg: "bg-blue-50",   border: "hover:border-blue-300",   href: "/trending-ai/models"    },
    { label: "AI Tools",        value: "40+",                      icon: Wrench,       color: "text-blue-500",  bg: "bg-blue-50",   border: "hover:border-blue-300",   href: "/ai-tools"              },
    { label: "AI Startups",     value: counts.startups  || "200+", icon: Rocket,       color: "text-indigo-600",bg: "bg-indigo-50", border: "hover:border-indigo-300", href: "/trending-ai/startups"  },
    { label: "AI Companies",    value: counts.companies || "100+", icon: Buildings,    color: "text-indigo-500",bg: "bg-indigo-50", border: "hover:border-indigo-300", href: "/trending-ai/companies" },
    { label: "Upcoming Events", value: counts.events    || "20+",  icon: CalendarBlank,color: "text-blue-600",  bg: "bg-blue-50",   border: "hover:border-blue-300",   href: "/trending-ai/events"    },
    { label: "Research",        value: "Papers",                   icon: BookOpen,     color: "text-slate-600", bg: "bg-slate-100", border: "hover:border-slate-300",  href: "/trending-ai/research"  },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
          Explore the Platform
        </span>
        <Link to="/trending-ai" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
          All sections <ArrowRight size={11} />
        </Link>
      </div>
      {compact ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 h-full">
          <div className="space-y-1">
            {destinations.map(({ label, value, icon: Icon, color, bg, href }) => (
              <Link
                key={href}
                to={href}
                className="group flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center shrink-0`}>
                  <Icon size={15} className={color} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{label}</div>
                  <div className="text-[11px] text-slate-400">{value}</div>
                </div>
                <ArrowRight size={12} className="text-slate-300 group-hover:text-blue-500 transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {destinations.map(({ label, value, icon: Icon, color, bg, border, href }) => (
            <Link
              key={href}
              to={href}
              className={`group bg-white border-2 border-slate-100 ${border} rounded-2xl p-4 text-center transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400/30`}
            >
              <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center mx-auto mb-2.5`}>
                <Icon size={20} className={color} weight="duotone" />
              </div>
              <div className="text-xl font-black text-slate-900 group-hover:text-blue-600 transition-colors leading-none mb-1">
                {value}
              </div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-tight">
                {label}
              </div>
              <div className="mt-2 text-[10px] font-semibold text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-0.5">
                Browse <ArrowRight size={9} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Hero Story ─────────────────────────────────────────────────────────────────

function HeroStory({ items }) {
  const story = items.find((n) => n.featured) || items[0];
  if (!story) return null;
  const slug = story.slug || story.id;

  return (
    <div className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-2xl overflow-hidden p-8 md:p-10 text-white group">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-3xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            <Fire size={9} weight="fill" /> BREAKING
          </span>
          {story.category && <Badge color="slate">{story.category}</Badge>}
          {story.publisher && <span className="text-xs text-slate-400">{story.publisher}</span>}
        </div>

        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-snug text-white mb-4">
          {stripHtml(story.title)}
        </h2>

        {story.excerpt && (
          <p className="text-slate-300 text-base leading-relaxed mb-6 max-w-2xl">
            {stripHtml(story.excerpt)}
          </p>
        )}

        <div className="flex items-center gap-4 flex-wrap">
          <Link
            to={`/trending-ai/news/${slug}`}
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            Read full story <ArrowRight size={14} />
          </Link>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            {story.views != null && (
              <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-2.5 py-1">
                <TrendUp size={11} weight="bold" /> {Number(story.views).toLocaleString()} views
              </span>
            )}
            {story.shares != null && (
              <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-2.5 py-1">
                <ArrowUpRight size={11} weight="bold" /> {Number(story.shares).toLocaleString()} shares
              </span>
            )}
            {story.readTime && (
              <span className="flex items-center gap-1.5 bg-white/10 rounded-full px-2.5 py-1">
                <Clock size={11} weight="bold" /> {story.readTime}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Can't Stop Reading (was: Trending Today) ───────────────────────────────────

function CantStopReading({ items }) {
  const sorted = [...items].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader
        title="Can't Stop Reading"
        subtitle="Most-shared AI stories right now"
        href="/trending-ai/news"
        hrefLabel="All stories"
      />
      <ol className="space-y-3.5">
        {sorted.map((story, i) => {
          const slug = story.slug || story.id;
          return (
            <li key={story.id} className="flex gap-3 group">
              <span className={`text-2xl font-black shrink-0 leading-none mt-0.5 tabular-nums ${
                i === 0 ? "text-blue-600" : i === 1 ? "text-blue-300" : "text-slate-150"
              }`} style={i > 1 ? { color: "#e8ecf0" } : {}}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/trending-ai/news/${slug}`}
                  className="text-sm font-semibold text-slate-800 hover:text-blue-600 leading-snug line-clamp-2 transition-colors block"
                >
                  {stripHtml(story.title)}
                </Link>
                <div className="flex items-center gap-2 mt-1">
                  {story.publisher && <span className="text-[11px] text-slate-400">{story.publisher}</span>}
                  {isNewToday(story) && <TodayBadge />}
                  {story.hot && <HotBadge />}
                  {story.views != null && (
                    <span className="text-[10px] text-slate-400 flex items-center gap-0.5 ml-auto shrink-0">
                      <TrendUp size={9} /> {Number(story.views).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── Just Dropped (was: Model Release Center) ──────────────────────────────────

function JustDropped({ items }) {
  const models = items.filter((m) => m.isNew || m.is_new || m.hot).slice(0, 4);
  const providerColors = {
    OpenAI:           "bg-emerald-100 text-emerald-800",
    Anthropic:        "bg-purple-100 text-purple-800",
    "Google DeepMind":"bg-blue-100 text-blue-800",
    "Meta AI":        "bg-indigo-100 text-indigo-800",
    DeepSeek:         "bg-amber-100 text-amber-800",
    "Mistral AI":     "bg-rose-100 text-rose-800",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader
        title="Just Dropped"
        subtitle="New AI model releases this week"
        href="/trending-ai/models"
        hrefLabel="All models"
      />
      <div className="space-y-3">
        {models.map((model) => {
          const slug = model.slug || model.id;
          return (
            <Link
              key={model.slug || model.id}
              to={`/trending-ai/models/${slug}`}
              className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-slate-50 transition-colors group border border-transparent hover:border-slate-200"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{model.name}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${providerColors[model.provider] || "bg-slate-100 text-slate-600"}`}>
                    {model.provider}
                  </span>
                  {isNewToday(model) && <TodayBadge />}
                  {model.hot && <HotBadge />}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                  {model.contextWindow && <span>{model.contextWindow}</span>}
                  {model.pricing?.input && <><span>·</span><span>{model.pricing.input} in</span></>}
                  {model.openSource && <Badge color="green">Open Source</Badge>}
                </div>
              </div>
              {model.benchmarks && (
                <div className="text-right shrink-0">
                  {model.benchmarks.MMLU && <div className="text-xs font-bold text-slate-700">MMLU {model.benchmarks.MMLU}</div>}
                  {model.benchmarks.HumanEval && <div className="text-[10px] text-slate-400">HumanEval {model.benchmarks.HumanEval}</div>}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Tools Everyone's Testing (was: Tool Spotlight) ───────────────────────────

function ToolsEveryoneTesting({ items = [] }) {
  const trending = items.filter((t) => t.hot || (t.score || 0) >= 85).slice(0, 3);
  const newTools = items.filter((t) => t.isNew || t.is_new).slice(0, 3);
  const toolLink = (t) => `/ai-tools/${t.slug || t.id}`;

  if (items.length === 0) return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Tools Everyone's Testing" subtitle="Trending & freshly launched" href="/ai-tools" hrefLabel="All tools" />
      <div className="py-6 text-center text-xs text-slate-400">Loading tools…</div>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Tools Everyone's Testing" subtitle="Trending & freshly launched" href="/ai-tools" hrefLabel="All tools" />
      {trending.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">🔥 Trending</p>
          <div className="space-y-2.5">
            {trending.map((tool) => (
              <Link key={tool.id || tool.slug} to={toolLink(tool)} className="flex items-center gap-3 group p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors">
                <span className="text-xl shrink-0">{tool.emoji || "🤖"}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors block truncate">{tool.name}</span>
                  {tool.by && <p className="text-[11px] text-slate-400 truncate">{tool.by}</p>}
                </div>
                {tool.score && (
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0">{tool.score}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
      {newTools.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">✨ New launches</p>
          <div className="space-y-2.5">
            {newTools.map((tool) => (
              <Link key={tool.id || tool.slug} to={toolLink(tool)} className="flex items-center gap-3 group p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors">
                <span className="text-xl shrink-0">{tool.emoji || "🤖"}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors block truncate">{tool.name}</span>
                  {tool.by && <p className="text-[11px] text-slate-400 truncate">{tool.by}</p>}
                </div>
                <NewBadge />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Watch These (was: Startup Watch) ──────────────────────────────────────────

function WatchThese({ items }) {
  const startups = items.filter((s) => s.hot || s.isNew || s.is_new).slice(0, 4);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Watch These" subtitle="Funded, launched, and moving fast" href="/trending-ai/startups" hrefLabel="All startups" />
      <div className="space-y-3">
        {startups.map((s) => {
          const slug = s.slug || s.id;
          return (
            <Link
              key={s.slug || s.id}
              to={`/trending-ai/startups/${slug}`}
              className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-slate-50 transition-colors group border border-transparent hover:border-slate-200"
            >
              <span className="text-2xl shrink-0">{s.emoji || "🚀"}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{s.name}</span>
                  {isNewToday(s) && <TodayBadge />}
                  {(s.isNew || s.is_new) && !isNewToday(s) && <NewBadge />}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {s.round && <Badge color="green">{s.round}</Badge>}
                  {s.category && <span className="text-[11px] text-slate-400">{s.category}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                {s.funding && <div className="text-sm font-bold text-emerald-600">{s.funding}</div>}
                {s.valuation && <div className="text-[10px] text-slate-400">val. {s.valuation}</div>}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Papers That Matter (was: Research Radar) ──────────────────────────────────

function PapersThatMatter({ items }) {
  const papers = items.filter((r) => r.isNew || r.is_new || r.hot).slice(0, 4);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Papers That Matter" subtitle="Breakthroughs from the lab" href="/trending-ai/research" hrefLabel="All research" />
      <div className="space-y-4">
        {papers.map((paper) => {
          const slug = paper.slug || paper.id;
          return (
            <div key={paper.id} className="group">
              <div className="flex items-start gap-2 mb-1">
                <div className="flex-1">
                  <Link
                    to={`/trending-ai/research/${slug}`}
                    className="text-sm font-semibold text-slate-800 hover:text-blue-600 transition-colors leading-snug line-clamp-2 block"
                  >
                    {paper.title}
                  </Link>
                </div>
                {paper.hot && <HotBadge />}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                {paper.institution && <span>{paper.institution}</span>}
                {paper.institution && paper.category && <span>·</span>}
                {paper.category && <Badge color="purple">{paper.category}</Badge>}
                {paper.citations != null && <><span>·</span><span className="font-semibold text-slate-500">{paper.citations} citations</span></>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Agents Going Mainstream (was: Agent Hub) ──────────────────────────────────

function AgentsGoingMainstream({ items }) {
  const agents = items.filter((a) => a.hot || a.isNew || a.is_new).slice(0, 4);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Agents Going Mainstream" subtitle="New frameworks & autonomous tools" href="/trending-ai/agents" hrefLabel="All agents" />
      <div className="space-y-3">
        {agents.map((agent) => {
          const slug = agent.slug || agent.id;
          return (
            <Link
              key={agent.slug || agent.id}
              to={`/trending-ai/agents/${slug}`}
              className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-slate-50 transition-colors group border border-transparent hover:border-slate-200"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <Robot size={16} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{agent.name}</span>
                  {(agent.isNew || agent.is_new) && <NewBadge />}
                  {agent.hot && <HotBadge />}
                </div>
                <p className="text-[11px] text-slate-400 truncate">
                  {(agent.by || agent.creator) ? `${agent.by || agent.creator} · ` : ""}{agent.category}
                </p>
              </div>
              {agent.pricing && (
                <Badge color={agent.openSource || agent.open_source ? "green" : "slate"}>{agent.pricing}</Badge>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Mark Your Calendar (was: Event Timeline) ──────────────────────────────────

function MarkYourCalendar({ items }) {
  const upcoming = items
    .filter((e) => new Date(e.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 4);

  const typeColors = {
    "Conference":    "bg-blue-100 text-blue-700",
    "Product Launch":"bg-purple-100 text-purple-700",
    "Summit":        "bg-amber-100 text-amber-700",
    "Festival":      "bg-rose-100 text-rose-700",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Mark Your Calendar" subtitle="Upcoming AI events & launches" href="/trending-ai/events" hrefLabel="All events" />
      <div className="space-y-3">
        {upcoming.map((event) => {
          const d = new Date(event.date);
          const slug = event.slug || event.id;
          return (
            <Link
              key={event.id}
              to={`/trending-ai/events/${slug}`}
              className="flex gap-3 group p-2 -mx-2 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <div className="w-11 shrink-0 text-center bg-slate-50 rounded-xl py-1.5">
                <div className="text-[9px] font-bold uppercase text-slate-400 leading-none">
                  {d.toLocaleString("default", { month: "short" })}
                </div>
                <div className="text-xl font-black text-slate-800 leading-none mt-0.5">{d.getDate()}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{event.name}</span>
                  {event.hot && <HotBadge />}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${typeColors[event.type] || "bg-slate-100 text-slate-600"}`}>
                    {event.type}
                  </span>
                  {event.location && <span className="text-[11px] text-slate-400">{event.location}</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Level Up (was: Learning Center) ───────────────────────────────────────────

function LevelUp({ items }) {
  const courses = items.filter((l) => l.featured || l.isFeatured || l.hot || l.isNew || l.is_new).slice(0, 4);
  const levelColors = {
    Beginner:     "bg-green-50 text-green-700",
    Intermediate: "bg-blue-50 text-blue-700",
    Advanced:     "bg-purple-50 text-purple-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full">
      <SectionHeader title="Level Up" subtitle="Top courses to stay current" href="/trending-ai/learning" hrefLabel="All courses" />
      <div className="space-y-3">
        {courses.map((c) => {
          const slug = c.slug || c.id;
          return (
            <Link
              key={c.id}
              to={`/trending-ai/learning/${slug}`}
              className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-slate-50 transition-colors group border border-transparent hover:border-slate-200"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <GraduationCap size={16} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors truncate">{c.title}</span>
                  {(c.isNew || c.is_new) && <NewBadge />}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {c.level && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${levelColors[c.level] || "bg-slate-100 text-slate-600"}`}>
                      {c.level}
                    </span>
                  )}
                  {(c.price || c.pricing) && <span className="text-[11px] text-slate-400">{c.price || c.pricing}</span>}
                  {c.rating && <span className="text-xs text-amber-600">★ {c.rating}</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Who's Making Moves (was: Market Movers) ───────────────────────────────────

function WhosMakingMoves({ items }) {
  const companies = items.filter((c) => c.trending || c.hot).slice(0, 5);
  const rankColors = ["border-amber-400", "border-slate-300", "border-amber-600", "border-slate-200", "border-slate-200"];
  const rankTextColors = ["text-amber-500", "text-slate-400", "text-amber-600/70", "text-slate-300", "text-slate-200"];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <SectionHeader title="Who's Making Moves" subtitle="Most active AI companies this week" href="/trending-ai/companies" hrefLabel="All companies" />
      <div className="space-y-2">
        {companies.map((c, i) => {
          const slug = c.slug || c.id;
          return (
            <Link
              key={c.slug || c.id}
              to={`/trending-ai/companies/${slug}`}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors group border-l-4 ${rankColors[i] || "border-slate-100"} bg-slate-50/50 hover:bg-slate-50 hover:shadow-sm`}
            >
              <span className={`text-base font-black shrink-0 w-5 tabular-nums ${rankTextColors[i] || "text-slate-200"}`}>
                {i + 1}
              </span>
              <span className="text-2xl shrink-0">{c.logo || "🏢"}</span>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors block">{c.name}</span>
                {c.recentNews && <p className="text-[11px] text-slate-400 truncate">{c.recentNews}</p>}
              </div>
              <div className="text-right shrink-0 flex items-center gap-2">
                {c.valuation && <div className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{c.valuation}</div>}
                {c.hot && <HotBadge />}
                <ArrowRight size={12} className="text-slate-300 group-hover:text-blue-400 transition-colors" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── "Go Deeper" destination grid CTA ──────────────────────────────────────────

function GoDeeper() {
  const cards = [
    {
      icon: Brain,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: "AI Models",
      desc: "Compare benchmarks, context windows, and pricing across 50+ models.",
      cta: "Browse all models",
      to: "/trending-ai/models",
    },
    {
      icon: Buildings,
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      title: "Company Intel",
      desc: "Track valuations, headcount signals, and recent moves from top AI labs.",
      cta: "Explore companies",
      to: "/trending-ai/companies",
    },
    {
      icon: CurrencyDollar,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: "Funding Tracker",
      desc: "Every funding round, seed to Series D — who raised, how much, and from whom.",
      cta: "See all rounds",
      to: "/trending-ai/funding",
    },
    {
      icon: BookOpen,
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      title: "Research Hub",
      desc: "Breakthrough papers from top labs — ranked by citations and community buzz.",
      cta: "Read the research",
      to: "/trending-ai/research",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-2xl p-8">
      <div className="text-center mb-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-blue-400">Go Deeper</span>
        <h2 className="text-2xl font-bold text-white mt-2">Ready to explore?</h2>
        <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto">
          The dashboard is just the surface. Dive into any section for the full picture.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ icon: Icon, iconBg, iconColor, title, desc, cta, to }) => (
          <Link
            key={to}
            to={to}
            className="group bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-blue-400/50 rounded-2xl p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400/30"
          >
            <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center mb-4`}>
              <Icon size={20} className={iconColor} weight="duotone" />
            </div>
            <h3 className="text-base font-bold text-white mb-1.5">{title}</h3>
            <p className="text-xs text-slate-400 leading-relaxed mb-4">{desc}</p>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 group-hover:text-blue-300 transition-colors">
              {cta} <ArrowRight size={11} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Sidebar: Jump-to nav ──────────────────────────────────────────────────────

const QUICK_NAV_SECTIONS = [
  { id: "section-news",      label: "Live AI News",       Icon: Pulse         },
  { id: "section-trending",  label: "Can't Stop Reading", Icon: Fire          },
  { id: "section-models",    label: "Just Dropped",       Icon: Brain         },
  { id: "section-startups",  label: "Watch These",        Icon: Rocket        },
  { id: "section-companies", label: "Who's Making Moves", Icon: Buildings     },
  { id: "section-events",    label: "Mark Your Calendar", Icon: CalendarBlank },
];

function QuickNav() {
  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Jump to Section</span>
      </div>
      <div className="p-2 space-y-0.5">
        {QUICK_NAV_SECTIONS.map(({ id, label, Icon }) => (
          <a
            key={id}
            href={`#${id}`}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <Icon size={13} className="shrink-0 text-slate-400" />
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

function LatestSignalsSidebar({ items }) {
  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
        </span>
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Latest Signals</span>
      </div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="px-4 py-3 animate-pulse">
              <div className="h-3 bg-slate-100 rounded w-4/5 mb-1.5" />
              <div className="h-2 bg-slate-100 rounded w-1/3" />
            </div>
          ))
        ) : (
          items.slice(0, 5).map((item, i) => (
            <a
              key={item.id || i}
              href={item.url || item.link || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-2.5 px-4 py-3 hover:bg-slate-50 group transition-colors"
            >
              <span className="text-[11px] font-bold text-blue-500 shrink-0 w-4 mt-0.5">{i + 1}</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 group-hover:text-blue-700 line-clamp-2 leading-snug transition-colors">
                  {item.title || item.text || item.summary}
                </p>
                {(item.source || item.type) && (
                  <span className="text-[11px] text-slate-400 mt-0.5 block uppercase tracking-wider">
                    {item.source || item.type}
                  </span>
                )}
              </div>
            </a>
          ))
        )}
      </div>
    </div>
  );
}

function ExplorePlatformSidebar({ counts }) {
  const links = [
    { to: "/trending-ai/models",    label: "AI Models",  badge: counts.models    > 0 ? `${counts.models}`    : null, color: "bg-blue-50 text-blue-700 border-blue-200"    },
    { to: "/ai-tools",              label: "AI Tools",   badge: null,                                                  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { to: "/trending-ai/funding",   label: "Funding",    badge: null,                                                  color: "bg-amber-50 text-amber-700 border-amber-200"  },
    { to: "/trending-ai/companies", label: "Companies",  badge: counts.companies > 0 ? `${counts.companies}` : null, color: "bg-purple-50 text-purple-700 border-purple-200" },
  ];
  return (
    <div className="border border-slate-200 rounded-2xl p-4">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-3">Explore Platform</span>
      <div className="space-y-2">
        {links.map(({ to, label, badge, color }) => (
          <Link
            key={to}
            to={to}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold border transition-opacity hover:opacity-75 ${color}`}
          >
            <span>{label}</span>
            <span className="flex items-center gap-1">
              {badge && <span className="font-bold">{badge}</span>}
              <ArrowRight size={11} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function TrendingAIPage() {
  const [liveData, setLiveData] = useState({});

  const aiFetchFn     = useCallback((page) => fetchTrending("technology", "US", page, 12), []);
  const aiCacheKey    = useCallback((page) => `ai_hub_tech_US_${page}`, []);
  const aiFeed        = useInfiniteFeed({ fetchFn: aiFetchFn, feedKey: "ai_hub_tech", getCacheKey: aiCacheKey, ttlMs: 15 * 60 * 1000 });
  const aiSentinelRef = useScrollObserver(aiFeed.loadMore);

  useEffect(() => {
    const SECTIONS = [
      { key: "news",      pageSize: 10 },
      { key: "models",    pageSize: 8  },
      { key: "agents",    pageSize: 8  },
      { key: "research",  pageSize: 8  },
      { key: "companies", pageSize: 8  },
      { key: "startups",  pageSize: 8  },
      { key: "funding",   pageSize: 10 },
      { key: "events",    pageSize: 8  },
      { key: "learning",  pageSize: 8  },
      { key: "tools",     pageSize: 20 },
      { key: "dashboard", pageSize: 10 },
    ];
    Promise.allSettled(
      SECTIONS.map(({ key, pageSize }) =>
        fetchAISection(key, { page: 1, page_size: pageSize })
      )
    ).then((results) => {
      const data = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          const items = Array.isArray(r.value) ? r.value : (r.value?.items ?? []);
          if (items.length > 0) data[SECTIONS[i].key] = items;
        }
      });
      if (Object.keys(data).length > 0) setLiveData(data);
    });
  }, []);

  const news       = liveData.news      || [];
  const models     = liveData.models    || [];
  const agents     = liveData.agents    || [];
  const research   = liveData.research  || [];
  const companies  = liveData.companies || [];
  const startups   = liveData.startups  || [];
  const funding    = liveData.funding   || [];
  const events     = liveData.events    || [];
  const learning   = liveData.learning  || [];
  const tools      = liveData.tools     || [];
  const pulseItems = liveData.dashboard || [];

  const counts = {
    models:       models.length,
    companies:    companies.length,
    startups:     startups.length,
    events:       events.filter((e) => new Date(e.date) >= new Date()).length,
    fundingTotal: (funding.reduce((s, f) => s + (f.amountNum || f.amount_num || 0), 0) / 1000).toFixed(1),
  };

  return (
    <>
      <SEO
        title="Trending AI News — TrendMing"
        description="The latest trending AI news, filtered and summarized by TrendMing. Free, updated daily."
        canonical="https://trendming.com/trending"
      />

      <AINavBar />

      {/* ── Dashboard + sticky sidebar ── */}
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 items-start">

          {/* Main content */}
          <div className="space-y-6 min-w-0">
            {/* ── Live AI News ── */}
            <section id="section-news" data-testid="ai-hub-news-section">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Live AI News</span>
                </div>
              </div>
              <InfiniteNewsFeed
                feed={aiFeed}
                sentinelRef={aiSentinelRef}
                heroFirst
                testId="ai-hub-feed"
              />
            </section>

            {/* ── Most-read + explore strip ── */}
            <div id="section-trending" className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <CantStopReading items={news} />
              </div>
              <div>
                <ExplorePlatform counts={counts} compact />
              </div>
            </div>

            {/* ── Models + Tools ── */}
            <div id="section-models" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <JustDropped items={models} />
              <ToolsEveryoneTesting items={tools} />
            </div>

            {/* ── Second 3-col grid ── */}
            <div id="section-startups" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <WatchThese items={startups} />
              <PapersThatMatter items={research} />
              <AgentsGoingMainstream items={agents} />
            </div>

            {/* ── Third 2-col grid ── */}
            <div id="section-events" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <MarkYourCalendar items={events} />
              <LevelUp items={learning} />
            </div>

            {/* ── Who's Making Moves ── */}
            <div id="section-companies">
              <WhosMakingMoves items={companies} />
            </div>

            {/* ── Go Deeper ── */}
            <GoDeeper />
          </div>

          {/* Sticky sidebar */}
          <aside className="sticky top-24 space-y-4 hidden lg:block">
            <QuickNav />
            <LatestSignalsSidebar items={news} />
            <ExplorePlatformSidebar counts={counts} />
          </aside>

        </div>
      </div>
    </>
  );
}
